import { useMemo } from 'react';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { getTypeOrNull, useClientsQuery } from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';
import { createFlagUpdatesChangeExtension } from '@spotify/plugin-confidence-flags-react';

type ClientItem = {
  name: string;
  displayName?: string;
};

const getClients = (value: unknown): ClientItem[] => {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    return [{ name: value }];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  const clients: ClientItem[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      clients.push({ name: item });
    } else if (
      typeof item === 'object' &&
      item !== null &&
      'name' in item &&
      typeof item.name === 'string'
    ) {
      const displayName =
        'displayName' in item && typeof item.displayName === 'string'
          ? item.displayName
          : undefined;
      clients.push({ name: item.name, displayName });
    }
  }

  return clients;
};

type ClientChanges = {
  oldClients: ClientItem[];
  newClients: ClientItem[];
  added: ClientItem[];
  removed: ClientItem[];
};

const analyzeClientChanges = (change: {
  oldValue?: unknown;
  newValue?: unknown;
}): ClientChanges => {
  const oldClients = getClients(change.oldValue);
  const newClients = getClients(change.newValue);

  const added = newClients.filter(
    n => !oldClients.some(o => o.name === n.name),
  );
  const removed = oldClients.filter(
    o => !newClients.some(n => n.name === o.name),
  );

  return { oldClients, newClients, added, removed };
};

const ClientsChangeRender = ({ change }: ActivityChangeOptions) => {
  const { added, removed } = analyzeClientChanges(change);

  const filterString = [
    ...new Set([...added.map(c => c.name), ...removed.map(c => c.name)]),
  ]
    .map(name => `name:"${name}"`)
    .join(' OR ');

  const { data } = useClientsQuery({
    variables: { filter: filterString },
    skip: !filterString,
    fetchPolicy: 'cache-first',
  });

  const displayNameMap = useMemo(() => {
    const clients = getTypeOrNull(data?.clients)?.clients ?? [];
    return new Map(clients.map(c => [c.name, c.displayName]));
  }, [data?.clients]);

  const getDisplayName = (client: ClientItem): string =>
    displayNameMap.get(client.name) ??
    client.displayName ??
    extractLastNameComponent(client.name) ??
    client.name;

  return (
    <>
      {added.length > 0 && (
        <ActivityItem.Section label="Added clients">
          {added.map(client => (
            <ActivityTagItem
              key={client.name}
              tagLabel={getDisplayName(client)}
              newValue=""
            />
          ))}
        </ActivityItem.Section>
      )}

      {removed.length > 0 && (
        <ActivityItem.Section label="Removed clients">
          {removed.map(client => (
            <ActivityTagItem
              key={client.name}
              tagLabel={getDisplayName(client)}
              oldValue=""
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

export const CLIENTS_CHANGE_EXTENSION = createFlagUpdatesChangeExtension({
  id: 'clients-change',
  predicate: ({ change }) => {
    if (change.fieldPath !== '/clients') {
      return false;
    }

    const { added, removed } = analyzeClientChanges(change);
    return added.length > 0 || removed.length > 0;
  },
  render: ClientsChangeRender,
});
